import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AppBar,
  AppBarProps,
  IconButton,
  Link,
  Toolbar,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useNav } from '../../../hooks/useNav';
import { ProxySession } from '../../ProxyTimer';
import AdminProxyBanner from './AdminProxyBanner';
import AppBarStyled from './AppBarStyled';

interface HeaderProps {
  headerContent?: ReactNode;
  logo?: ReactNode;
  appBarColor?: string;
  drawerWidth?: number;
  enableAdminProxyBanner?: boolean;
  proxySession?: ProxySession | null;
  onEndProxySession?: () => Promise<void>;
  onRedirect?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  headerContent,
  logo,
  appBarColor = 'inherit',
  drawerWidth = 260,
  enableAdminProxyBanner = false,
  proxySession,
  onEndProxySession,
  onRedirect,
}) => {
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));
  const { setDrawerOpen, drawerOpen } = useNav();
  const { user: auth0User } = useAuth0();

  // Check if user is an admin proxying in
  const isAdminProxying = auth0User?.sub?.startsWith('okta|armhr-ops');

  // common header
  const mainHeader: ReactNode = (
    <>
      {isAdminProxying && enableAdminProxyBanner && (
        <AdminProxyBanner
          proxySession={proxySession}
          onEndProxySession={onEndProxySession}
          onRedirect={onRedirect}
        />
      )}
      <Toolbar>
        {!drawerOpen && downLG && logo && (
          <Link
            component={RouterLink}
            to={'/dashboard'}
            underline="none"
            sx={{ display: 'flex', alignItems: 'center', mr: 1 }}
          >
            {logo}
          </Link>
        )}
        <IconButton
          aria-label="open drawer"
          onClick={() => setDrawerOpen(!drawerOpen)}
          edge="start"
          sx={{ ml: { xs: 0, lg: -2 }, mr: 1 }}
        >
          {!drawerOpen ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </IconButton>
        {headerContent}
      </Toolbar>
    </>
  );

  // app-bar params
  const appBar: AppBarProps = {
    position: 'fixed',
    color: appBarColor as
      | 'inherit'
      | 'primary'
      | 'secondary'
      | 'default'
      | 'transparent'
      | undefined,
    elevation: 0,
    sx: {
      borderBottom: `1px solid ${theme.palette.divider}`,
      zIndex: 1200,
      width: drawerOpen
        ? `calc(100% - ${drawerWidth}px)`
        : { xs: '100%', lg: `calc(100% - ${theme.spacing(7.5)})` },
    },
  };

  return (
    <>
      {!downLG ? (
        <AppBarStyled open={drawerOpen} drawerWidth={drawerWidth} {...appBar}>
          {mainHeader}
        </AppBarStyled>
      ) : (
        <AppBar {...appBar}>{mainHeader}</AppBar>
      )}
    </>
  );
};

export default Header;
